import { useCallback } from 'react'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { UserModel } from '../../../auth'
import { declineMatchingPaymentTicket, finishMatchingPaymentTicket } from '../../core/_requests'
import { getErrorStatus, getErrorText } from '../../../../common/api/errors'

const AppSwal = withReactContent(Swal)

type Options = {
  token: string
  /** Сумма к переводу — она и подтверждается, а не исходный запрос мерчанта. */
  amount?: number | null
  currentUser?: UserModel
  /** Вызывается после успешной операции: список убирает строку, карточка перезагружается. */
  onDone?: () => void
  /** Право на операции над тикетом. Без него действия не выполняются. */
  enabled: boolean
}

type DepositAction = 'complete' | 'decline'

const STATE_TEXT: Record<DepositAction, string> = {
  complete: 'Тикет не может быть завершен',
  decline: 'Тикет не может быть отменен',
}

const FALLBACK_TEXT: Record<DepositAction, string> = {
  complete: 'Произошла ошибка при завершении тикета',
  decline: 'Произошла ошибка при отмене тикета',
}

/** Отказ ручного завершения / отмены: 422 — тикет в неподходящем статусе, иначе причина бэкенда. */
export const depositActionErrorText = (err: unknown, action: DepositAction): string =>
  getErrorStatus(err) === 422 ? STATE_TEXT[action] : getErrorText(err, FALLBACK_TEXT[action], { withFieldName: false })

/**
 * Ручное завершение и отмена депозита.
 *
 * Вынесено из строки активного матчингового списка, чтобы у операции был один путь: список и обе
 * карточки зовут один и тот же код. Второй путь к той же операции — это расхождение в текстах,
 * в разборе кодов ответа и в правах, которое замечают уже в проде.
 */
export const useDepositTicketActions = ({ token, amount, currentUser, onDone, enabled }: Options) => {
  const complete = useCallback(async () => {
    if (!enabled) return

    const operationalAmount = amount || 0

    await AppSwal.fire({
      title: 'Вы точно хотите завершить тикет?',
      text: `Сумма к переводу: ${Number(operationalAmount).toFixed(2)}`,
      showCancelButton: true,
      cancelButtonText: 'Нет',
      confirmButtonText: 'Да, завершить',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const response = await finishMatchingPaymentTicket(
            token,
            currentUser,
            '',
            operationalAmount ? parseFloat(operationalAmount.toString()) : undefined,
          )

          if (response.data?.success) {
            onDone?.()

            return true
          }

          AppSwal.fire({ title: 'Ошибка', text: depositActionErrorText(response, 'complete'), icon: 'error' })

          return false
        } catch (err: unknown) {
          if (getErrorStatus(err) === 422) {
            AppSwal.fire({ title: '', text: depositActionErrorText(err, 'complete'), icon: 'warning' })
          } else {
            AppSwal.fire({ title: 'Ошибка', text: depositActionErrorText(err, 'complete'), icon: 'error' })
          }

          return false
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed && result.value === true) {
        AppSwal.fire({ title: 'Тикет завершен успешно!', timer: 1500 })
      }
    })
  }, [enabled, amount, token, currentUser, onDone])

  const decline = useCallback(async () => {
    if (!enabled) return

    await AppSwal.fire({
      title: 'Вы точно хотите отменить тикет?',
      showCancelButton: true,
      cancelButtonText: 'Нет',
      confirmButtonText: 'Да, отменить',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const response = await declineMatchingPaymentTicket(token, currentUser)

          if (response.data?.success) {
            onDone?.()

            return true
          }

          AppSwal.fire({ title: 'Ошибка', text: depositActionErrorText(response, 'decline'), icon: 'error' })

          return false
        } catch (err: unknown) {
          if (getErrorStatus(err) === 422) {
            AppSwal.fire({ title: '', text: depositActionErrorText(err, 'decline'), icon: 'warning' })
          } else {
            AppSwal.fire({ title: 'Ошибка', text: depositActionErrorText(err, 'decline'), icon: 'error' })
          }

          return false
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    })
  }, [enabled, token, currentUser, onDone])

  return { complete, decline }
}
